/**
 * 染色工具函数
 * 参考 iOS: ios/Sources/Atlas/AvatarConfig.swift
 */

// 颜色结构（RGBA，0-1）
export interface ToningColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

// Two Color Tint 结构
export interface TwoColorTint {
  light: ToningColor; // 亮部颜色
  dark: ToningColor;  // 暗部颜色
}

/** 主模版 + 关联模版的染色方案，按 id 去重（主模版优先） */
export function collectToningSchemes(
  mainConfig: { tonings?: any[] } | null | undefined,
  relatedTemplates?: { config?: { tonings?: any[] } }[],
): any[] {
  const byId = new Map<string, any>();
  for (const t of mainConfig?.tonings || []) {
    if (t?.id) byId.set(t.id, t);
  }
  for (const rel of relatedTemplates || []) {
    for (const t of rel.config?.tonings || []) {
      if (t?.id && !byId.has(t.id)) byId.set(t.id, t);
    }
  }
  return [...byId.values()];
}

/**
 * 从 Toning 方案构建 Two Color Tint
 * @param toning - 调色方案
 * @param colorId - 可选，指定某个 ColorSet.id；为空则取第一个
 */
export function buildTwoColorTint(
  toning: any,
  colorId?: string
): TwoColorTint | null {
  // 选择一个具体的 ColorSet
  let colorSet: any | undefined;
  if (colorId) {
    colorSet = toning.colors.find((c: any) => c.id === colorId);
  }
  if (!colorSet) {
    colorSet = toning.colors[0];
  }
  if (!colorSet) return null;

  // 优先使用新格式 light/dark 字段
  if (colorSet.light && colorSet.dark) {
    return {
      light: hexToColor(colorSet.light),
      dark: hexToColor(colorSet.dark),
    };
  }

  // 兼容旧格式：从 colors 数组中提取
  if (colorSet.colors && colorSet.colors.length > 0) {
    const stops = [...colorSet.colors].sort((a: any, b: any) => a.offset - b.offset);
    const lightStop = stops.find((s: any) => s.offset <= 0.1) || stops[0];
    const darkStop = stops.find((s: any) => s.offset >= 0.9) || stops[stops.length - 1];
    
    return {
      light: hexToColor(lightStop.color),
      dark: hexToColor(darkStop.color),
    };
  }

  return null;
}

/**
 * 在线性渐变中按 offset 采样颜色（0~1）
 */
function sampleColor(t: number, stops: any[]): ToningColor {
  const clampedT = Math.max(0, Math.min(1, t));
  const first = stops[0];
  const last = stops[stops.length - 1];

  if (!first || !last) {
    return { r: 1, g: 1, b: 1, a: 1 };
  }

  // 边界：小于最小 offset 或大于最大 offset，直接取端点色
  if (clampedT <= first.offset) {
    return hexToColor(first.color);
  }
  if (clampedT >= last.offset) {
    return hexToColor(last.color);
  }

  // 查找所在的渐变区间
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (clampedT >= a.offset && clampedT <= b.offset) {
      const span = b.offset - a.offset;
      const localT = span > 0 ? (clampedT - a.offset) / span : 0;
      return lerpColor(hexToColor(a.color), hexToColor(b.color), localT);
    }
  }

  return hexToColor(last.color);
}

/**
 * Hex (#RRGGBB 或 #RRGGBBAA) 转 ToningColor
 */
function hexToColor(hex: string): any {
  let cleaned = hex.trim();
  if (cleaned.startsWith("#")) {
    cleaned = cleaned.slice(1);
  }

  const value = parseInt(cleaned, 16);

  let r: number, g: number, b: number, a: number;

  if (cleaned.length === 8) {
    // RRGGBBAA
    r = ((value >> 24) & 0xff) / 255;
    g = ((value >> 16) & 0xff) / 255;
    b = ((value >> 8) & 0xff) / 255;
    a = (value & 0xff) / 255;
  } else if (cleaned.length === 6) {
    // RRGGBB
    r = ((value >> 16) & 0xff) / 255;
    g = ((value >> 8) & 0xff) / 255;
    b = (value & 0xff) / 255;
    a = 1;
  } else {
    // 无效格式，返回白色
    return { r: 1, g: 1, b: 1, a: 1 };
  }

  return { r, g, b, a };
}

/**
 * 线性插值两个颜色
 */
function lerpColor(
  from: ToningColor,
  to: ToningColor,
  t: number
): ToningColor {
  const f = Math.max(0, Math.min(1, t));
  return {
    r: from.r + (to.r - from.r) * f,
    g: from.g + (to.g - from.g) * f,
    b: from.b + (to.b - from.b) * f,
    a: from.a + (to.a - from.a) * f,
  };
}

